import {useCallback} from 'react'
import {Button} from '@primer/react'
import {GitBranchIcon} from '@primer/octicons-react'
import {Link} from '@github-ui/react-router'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {dismissRepositoryNoticePathPath, newRulesetPath} from '@github-ui/paths'
import {useCurrentUser} from '../contexts/CurrentUserContext'
import {useCurrentRepository} from '@github-ui/current-repository'
import {useCreateBranchButtonOptions} from '../contexts/CreateBranchButtonOptionContext'

import styles from './ProtectThisBranchBanner.module.css'
import {clsx} from 'clsx'

export function ProtectThisBranchBanner({onDismiss}: {onDismiss: () => void}) {
  const currentUser = useCurrentUser()
  const {defaultBranch, ownerLogin, name, id} = useCurrentRepository()
  const {helpUrl} = useCreateBranchButtonOptions()
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const dismissNotice = useCallback(() => {
    if (currentUser) {
      const fetch = async () => {
        const response = await verifiedFetch(
          `${dismissRepositoryNoticePathPath(currentUser)}?notice_name=sculk_protect_this_branch&repository_id=${id}`,
          {
            method: 'DELETE',
          },
        )
        if (response.ok) {
          onDismiss()
        }
      }
      fetch()
    }
  }, [currentUser, id, onDismiss])
  const trackDismissal = useCallback(() => {
    sendClickAnalyticsEvent({
      category: 'suggestion',
      action: 'click_to_dismiss',
      label: `ref_cta:dismiss;ref_loc:repo_branches_listing;`,
    })
  }, [sendClickAnalyticsEvent])

  const trackLearnMore = useCallback(() => {
    sendClickAnalyticsEvent({
      category: 'suggestion',
      action: `click_to_learn_more_about_rulesets`,
      label: `ref_cta:learn_more_about_rulesets;ref_loc:repo_files_listing;`,
    })
  }, [sendClickAnalyticsEvent])

  const trackProtectBranch = useCallback(() => {
    sendClickAnalyticsEvent({
      category: 'suggestion',
      action: 'click_to_add_a_rule',
      label: `ref_cta:protect_this_branch;ref_loc:repo_branches_listing;`,
    })
  }, [sendClickAnalyticsEvent])

  return (
    <aside
      className={clsx('color-border-default js-notice', styles.BannerContainer)}
      aria-label={`Your ${defaultBranch} branch isn't protected`}
    >
      <div className={styles.BannerContent} data-testid="protect-this-branch-banner">
        <div className={styles.ContentRow}>
          <div
            data-testid="protect-this-branch-primer-icon"
            className={clsx(
              /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
              'branch-action-item-icon completeness-indicator completeness-indicator-problem',
              styles.ShieldIcon,
            )}
          >
            <GitBranchIcon />
          </div>
          <div>
            <h3 className={clsx('h5', styles.BannerTitle)}>Your {defaultBranch} branch isn&apos;t protected</h3>
            <p className={styles.BannerText}>
              Protect this branch from force pushing or deletion, or require status checks before merging.&nbsp;
              <Link
                onClick={trackLearnMore}
                to={`${helpUrl}/${'repositories/configuring-branches-and-merges-in-your-repository/managing-rulesets/about-rulesets'}`}
                style={{
                  textDecoration: 'underline',
                }}
              >
                Learn more
              </Link>
            </p>
          </div>
        </div>
        <div className={styles.ContentRow}>
          <Button
            variant="invisible"
            onClick={() => {
              dismissNotice()
              trackDismissal()
            }}
            className={styles.DismissButton}
          >
            Dismiss
          </Button>
          <Link
            onClick={trackProtectBranch}
            to={newRulesetPath({
              owner: ownerLogin,
              repo: name,
            })}
            className="btn btn-sm"
          >
            Protect this branch
          </Link>
        </div>
      </div>
    </aside>
  )
}
